<template>
    <div class="modal_cont store_find_modal">
        <div class="modal_header">
            GS25 매장찾기
            <button type="button" class="btn_close" aria-label="닫기" @click="closeModal"></button>
        </div>

        <div class="modal_content">
            <div class="sf_row sf_row_dd">
                <select
                    v-model="searchCondition.sido"
                    class="sf_select"
                    :class="{ sf_is_placeholder: !searchCondition.sido }"
                    aria-label="지역 선택"
                    @change="selectSidoComboChange"
                >
                    <option value="" disabled>지역 선택</option>
                    <option v-for="sido in sidoOptions" :key="sido.value" :value="sido.value">
                        {{ sido.label }}
                    </option>
                </select>
                <select
                    v-model="searchCondition.gungu"
                    class="sf_select"
                    :class="{ sf_is_placeholder: !searchCondition.gungu }"
                    aria-label="시군구 선택"
                    @change="selectGunguComboChange"
                >
                    <option value="" disabled>시군구 선택</option>
                    <option v-for="gungu in gunguList" :key="gungu.gunguCd" :value="gungu.gunguCd">
                        {{ gungu.gunguNm }}
                    </option>
                </select>
                <select
                    v-model="searchCondition.dong"
                    class="sf_select"
                    :class="{ sf_is_placeholder: !searchCondition.dong }"
                    aria-label="동 선택"
                >
                    <option value="" disabled>동 선택</option>
                    <option v-for="dong in dongList" :key="dong.dongCd" :value="dong.dongCd">
                        {{ dong.dongNm }}
                    </option>
                </select>
            </div>

            <Search
                v-model="searchData"
                :search_opt="searchOptions"
                :placeholder="placeholder"
                :useSelect="false"
                :isDisabled="loading"
                @search="handleSearch"
            />

            <div class="sf_filter">
                <button
                    type="button"
                    class="sf_filter_btn"
                    :aria-expanded="filterExpanded"
                    @click="filterExpanded = !filterExpanded"
                >
                    <span class="sf_filter_label">필터</span>
                    <span class="sf_chevron" :class="{ is_collapsed: !filterExpanded }"></span>
                </button>
                <div v-show="filterExpanded" class="sf_chip_wrap">
                    <button
                        v-for="filter in serviceFilters"
                        :key="filter.code"
                        type="button"
                        class="sf_chip"
                        :class="{ is_selected: selectedServiceCodes.includes(filter.code) }"
                        @click="toggleServiceFilter(filter.code)"
                    >
                        {{ filter.label }}
                    </button>
                </div>
            </div>

            <div class="sf_map_canvas">
                <div class="inner">
                    <div class="store_list">
                        <div v-if="loading || errorMessage || !stores.length" class="no_result">
                            <p v-if="loading" class="type_1">매장 정보를 불러오는 중입니다.</p>
                            <p v-else-if="errorMessage" class="type_2">{{ errorMessage }}</p>
                            <p v-else :class="canSearch ? 'type_2' : 'type_1'" v-html="noDataMessage"></p>
                        </div>

                        <template v-else>
                            <dl
                                v-for="(store, idx) in stores"
                                :key="store.shopCode || store.shopName || idx"
                                class="item"
                                :class="{ active: selectedStore && selectedStoreKey(selectedStore) === selectedStoreKey(store) }"
                                @click="selectStore(store)"
                            >
                                <dt>{{ store.shopName || "-" }}</dt>
                                <dd>
                                    <p>{{ store.rnAddress || store.address || "주소 정보 없음" }}</p>
                                    <ul v-if="storeFilterNames(store).length">
                                        <li v-for="filterName in storeFilterNames(store)" :key="filterName">
                                            <em>{{ filterName }}</em>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </template>
                    </div>

                    <div class="map_canvas">
                        <KakaoMap
                            :key="`pc-${mapKey}`"
                            :center="center"
                            :level="4"
                            :height="'100%'"
                            :marker="false"
                            :addresses="selectedStore ? selectedMapAddresses : mapAddresses"
                            @error="handleMapError"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import KakaoMap from "@/components/KakaoMap.vue";
import Search from "@/components/Search.vue";
import { brandAPI } from "@/api/brandAPI.js";
import { gisAPI } from "@/api/gisAPI.js";
import modal from "@/utils/popupModal";
import { COMM_CD_GROUP_IDS, loadCommCdGroupItems } from "@/utils/CommonCode.js";

const DEFAULT_CENTER = {
    lat: 37.566826,
    lng: 126.9786567
};


export default {
    name: "StoreFindModal",
    components: {
        KakaoMap,
        Search
    },
    data() {
        return {
            center: DEFAULT_CENTER,
            stores: [],
            loading: false,
            errorMessage: "",
            selectedStore: null,
            mapKey: 0,
            filterExpanded: false,
            sidoOptions: [],
            gunguList: [],
            dongList: [],
            serviceFilters: [],
            selectedServiceCodes: [],
            placeholder: "찾으시려는 매장명을 입력하세요.",
            searchOptions: [],
            searchData: {
                type: "",
                keyword: ""
            },
            searchCondition: this.createSearchCondition()
        };
    },
    computed: {
        markerStores() {
            if (this.selectedStore) {
                return [this.selectedStore];
            }

            return Array.isArray(this.stores) ? this.stores.slice(0, 50) : [];
        },
        mapAddresses() {
            return this.markerStores
                .filter((store) => store.rnAddress || store.address)
                .map((store) => ({
                    title: store.shopName,
                    address: store.rnAddress || store.address
                }));
        },
        selectedMapAddresses() {
            if (!this.selectedStore) return [];

            return [
                {
                    title: this.selectedStore.shopName,
                    address: this.selectedStore.rnAddress || this.selectedStore.address
                }
            ].filter((store) => store.address);
        },
        mapLimited() {
            return !this.selectedStore && this.stores.length > this.mapAddresses.length;
        },
        hasLocationSearchCondition() {
            return Boolean(this.searchCondition.sido && this.searchCondition.gungu);
        },
        hasShopNameSearchCondition() {
            return Boolean(this.searchCondition.shopName.trim());
        },
        canSearch() {
            return this.hasLocationSearchCondition || this.hasShopNameSearchCondition;
        },
        noDataMessage() {
            return this.canSearch ? "검색결과가 없습니다." : "매장명을 입력하여<br/> 검색해 주세요.";
        }
    },
    mounted() {
        this.loadSidoOptions();
    },
    methods: {
        async loadSidoOptions() {
            const codeItems = await loadCommCdGroupItems({
                sidoItems: COMM_CD_GROUP_IDS.GS25_BRFS_REGN_DV_CD,
                serviceItems: COMM_CD_GROUP_IDS.STR_CHRC_CD
            });

            this.sidoOptions = codeItems.sidoItems.map((item) => ({
                value: item.value,
                label: item.title
            }));

            this.serviceFilters = codeItems.serviceItems.map((item) => ({
                code: item.value,
                label: item.title
            }));
        },
        createSearchCondition() {
            return {
                shopCode: "",
                sido: "",
                gungu: "",
                dong: "",
                shopName: "",
                rnAddress: "",
                strProtyCd: "",
                strProtyNm: ""
            };
        },
        closeModal(event) {
            modal.close(event.currentTarget);
        },
        async selectSidoComboChange() {
            this.searchCondition.gungu = "";
            this.searchCondition.dong = "";
            this.gunguList = [];
            this.dongList = [];

            if (!this.searchCondition.sido) return;

            try {
                const response = await gisAPI.searchGungu({ sido: this.searchCondition.sido });
                if (response.data.resultCode === "00000") {
                    this.gunguList = response.data.result || [];
                }
            } catch (error) {
                console.error("시군구 목록 조회 오류:", error);
            }
        },
        async selectGunguComboChange() {
            this.searchCondition.dong = "";
            this.dongList = [];

            if (!this.searchCondition.gungu) return;

            try {
                const response = await gisAPI.searchDong({ gungu: this.searchCondition.gungu });
                if (response.data.resultCode === "00000") {
                    this.dongList = response.data.result || [];
                }
            } catch (error) {
                console.error("동 목록 조회 오류:", error);
            }
        },
        async handleSearch(searchValue = this.searchData) {
            if (searchValue && typeof searchValue === "object") {
                this.searchData = {
                    type: searchValue.type || "",
                    keyword: searchValue.keyword || ""
                };
                this.searchCondition.shopName = (searchValue.keyword || "").trim();
            }

            if (!this.canSearch) {
                const message = "지역/시군구를 선택하거나 매장명을 입력해 주세요.";
                this.errorMessage = message;
                alert(message);
                return;
            }

            this.loading = true;
            this.errorMessage = "";
            this.selectedStore = null;

            try {
                const response = await brandAPI.selectGs25Stores(this.createSearchParams());
                this.stores = this.normalizeStores(response);
                this.mapKey += 1;
            } catch (error) {
                this.stores = [];
                this.errorMessage = "매장 정보를 불러오지 못했습니다.";
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        handleReset() {
            this.searchCondition = this.createSearchCondition();
            this.searchData = {
                type: "",
                keyword: ""
            };
            this.selectedServiceCodes = [];
            this.gunguList = [];
            this.dongList = [];
            this.handleSearch();
        },
        toggleServiceFilter(code) {
            if (this.selectedServiceCodes.includes(code)) {
                this.selectedServiceCodes = this.selectedServiceCodes.filter((selectedCode) => selectedCode !== code);
                return;
            }

            this.selectedServiceCodes = [...this.selectedServiceCodes, code];
        },
        createSearchParams() {
            const selectedServices = this.serviceFilters.filter((filter) =>
                this.selectedServiceCodes.includes(filter.code)
            );

            return {
                ...this.searchCondition,
                strProtyCd: selectedServices.map((service) => service.code).join(","),
                strProtyNm: selectedServices.map((service) => service.label).join(",")
            };
        },
        selectStore(store) {
            this.selectedStore = store;
            this.mapKey += 1;
        },
        clearSelectedStore() {
            this.selectedStore = null;
            this.mapKey += 1;
        },
        selectedStoreKey(store) {
            return store?.shopCode || store?.shopName || store?.rnAddress || store?.address;
        },
        splitStoreProperty(value) {
            if (Array.isArray(value)) {
                return value.map((item) => String(item).trim()).filter(Boolean);
            }

            return String(value || "")
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);
        },
        getServiceFilterLabel(code) {
            const matchedFilter = this.serviceFilters.find((filter) => String(filter.code) === String(code));
            return matchedFilter?.label || code;
        },
        storeFilterNames(store) {
            const propertyNames = this.splitStoreProperty(store?.strProtyNm);
            if (propertyNames.length) return propertyNames;

            return this.splitStoreProperty(store?.strProtyCd).map((code) => this.getServiceFilterLabel(code));
        },
        normalizeStores(response) {
            const data = response?.data;

            if (Array.isArray(data)) return data;
            if (Array.isArray(data?.data)) return data.data;
            if (Array.isArray(data?.result)) return data.result;
            if (Array.isArray(data?.list)) return data.list;
            if (Array.isArray(data?.items)) return data.items;
            if (Array.isArray(data?.data?.list)) return data.data.list;
            if (Array.isArray(data?.data?.result)) return data.data.result;

            return [];
        },
        handleMapError(error) {
            this.errorMessage = error.message;
        }
    }
};
</script>

<style scoped>
/* Figma 198:26591 */

.store_find_modal.modal_cont {
    width: 844px;
    max-width: 100%;
    min-height: 0;
    box-sizing: border-box;
}

/* title FRAME h92 */
.sf_title_row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    min-height: 92px;
    margin: 0;
    padding: 0;
}

.sf_title {
    margin: 0;
    color: #161616;
    font-size: 4rem;
    font-weight: 700;
    line-height: 52px;
    letter-spacing: -0.4px;
}

.sf_close {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    margin-top: 6px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    position: relative;
}

.sf_close::before,
.sf_close::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20px;
    height: 0;
    border-top: 2px solid #2d2d2d;
    transform-origin: center;
}

.sf_close::before {
    transform: translate(-50%, -50%) rotate(45deg);
}

.sf_close::after {
    transform: translate(-50%, -50%) rotate(-45deg);
}

.sf_body {
    padding: 0;
    margin: 0;
}

/* 3x276 + gap8x2 = 844 */
.sf_row {
    margin: 0;
    padding: 0;
}

.sf_row_dd {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
}

.sf_select {
    min-width: 0;
    height: 52px;
    padding: 0 40px 0 16px;
    border: 1px solid #c4c4d0;
    border-radius: 12px;
    background-color: #fff;
    color: #161616;
    font-size: 1.6rem;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.16px;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Cpath fill='none' stroke='%23666' stroke-width='1.5' d='M5 7.5L10 12.5 15 7.5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    box-sizing: border-box;
}

.sf_select.sf_is_placeholder {
    color: #a4a4b0;
}

.sf_select option {
    color: #161616;
}

.sf_input {
    width: 100%;
    height: 52px;
    box-sizing: border-box;
    padding: 0 44px 0 16px;
    border: 1px solid #c4c4d0;
    border-radius: 12px;
    background: #fff;
    color: #161616;
    font-size: 1.6rem;
    line-height: 24px;
    letter-spacing: -0.16px;
    outline: none;
}

.sf_input::placeholder {
    color: #a4a4b0;
}

.sf_input:focus {
    border-color: #107af2;
}

.sf_filter {
    margin-top: 12px;
}

.sf_filter_btn {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    margin: 0 0 6px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
}

.sf_filter_label {
    color: #161616;
    font-size: 1.6rem;
    font-weight: 400;
    line-height: 1.5;
    letter-spacing: -0.01em;
}

.sf_chevron {
    width: 16px;
    height: 16px;
    background:url('@/assets/images/common/icon_set_16.png') -136px -14px no-repeat;
    background-size:auto 83px;
    transition:transform 0.25s;
}

.sf_chevron.is_collapsed {
    transform: rotate(180deg) translateY(2px);
}

.sf_chip_wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    min-height: 124px;
    align-content: flex-start;
}

.sf_chip {
    height: 36px;
    padding: 0 16px;
    box-sizing: border-box;
    border-radius: 99px;
    border: 1px solid #d7d7df;
    background: #f2f2f4;
    color: #666;
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 19.6px;
    letter-spacing: -0.14px;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.sf_chip.is_selected {
    border-color: #107af2;
    background: #e7f2fe;
    color: #107af2;
}

.sf_chip.has_icon::before {
    content: "";
    display: inline-block;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-right: 4px;
    background: var(--chip-icon) center / contain no-repeat;
}

.sf_map_canvas {
    margin-top: 64px;
    width: 100%;
    height: 320px;
    border-radius: 10px;
    overflow: hidden;
}

.sf_map_canvas .inner {
    display: flex;
    gap: 20px;
}

.sf_map_canvas .map_canvas {
    min-height: 320px;
    background-color: #d7d7df;
    border-radius: 10px;
    flex: 1;
    overflow: hidden;
}

.sf_map_canvas .map_canvas :deep(.kakao-map) {
    height: 100%;
    border: 0;
    border-radius: 10px;
}

.sf_map_canvas .store_list {
    max-height: 320px;
    overflow-y: auto;
    flex: 1;
}

.sf_map_canvas .store_list dl {
    padding: 16px 20px;
    border-top: 1px solid #d7d7df;
    border-bottom: 1px solid #d7d7df;
    cursor: pointer;
}

.sf_map_canvas .store_list dl.active {
    background-color: #f8f9fb;
}

.sf_map_canvas .store_list dt {
    color: #161616;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 135%;
}

.sf_map_canvas .store_list dd {
    margin-top: 8px;
}

.sf_map_canvas .store_list dd p {
    color: #67676f;
    font-size: 1.6rem;
    letter-spacing: -0.01em;
    line-height: 150%;
}

.sf_map_canvas .store_list dd ul {
    margin-top: 6px;
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
}

.sf_map_canvas .store_list dd ul em {
    padding: 3px 6px;
    color: #107af2;
    font-size: 1.4rem;
    letter-spacing: -0.01em;
    line-height: 140%;
    background-color: #e7f2fe;
    border-radius: 4px;
    display: block;
}

.sf_map_canvas .store_list .no_result {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.sf_map_canvas .store_list .no_result p {
    height: 100%;
    color: #161616;
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 150%;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}

.sf_map_canvas .store_list .no_result p:before {
    width: 40px;
    height: 40px;
    margin-bottom: 16px;
    background-image: url('@/assets/images/sub/icon_cont_40.png');
    background-repeat: no-repeat;
    content: '';
    display: block;
}

.sf_map_canvas .store_list .no_result p.type_1:before {
    background-position: -20px -20px;
}

.sf_map_canvas .store_list .no_result p.type_2:before {
    background-position: -260px -346px;
}

.sf_result_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e5e5e9;
    color: #161616;
    font-size: 1.6rem;
}

.sf_result_header span {
    color: #67676f;
    font-size: 1.4rem;
}

.sf_reset_btn {
    height: 36px;
    padding: 0 14px;
    border: 1px solid #c4c4d0;
    border-radius: 8px;
    background: #fff;
    color: #161616;
    cursor: pointer;
}

.sf_message {
    margin: 16px 0 0;
    color: #666;
    font-size: 1.5rem;
    text-align: center;
}

.sf_message_error {
    color: #d32f2f;
}

.store_layout_flex { display: flex; align-items: flex-start; gap: 32px; margin-top: 16px; border-top: 1px solid #161618; }
.store_list_side { flex: 1; width: 100%; max-height: 600px; overflow-y: auto; }
.store_item { padding: 24px 0; border-bottom: 1px solid #e5e5e9; }
.store_item.active { background-color: #f8f9fb; }
.store_item_btn { width: 100%; padding: 0; border: none; background: transparent; text-align: left; cursor: pointer; display: flex; flex-direction: column; gap: 8px; }
.store_name { color: #161616; font-size: 2.4rem; font-weight: 700; line-height: 1.35; display: block; }
.store_name::after { content: ""; width: 20px; height: 20px; margin-left: 8px; background:url('@/assets/images/common/icon_set_20.png') -324px -25px no-repeat; display: inline-block; vertical-align: middle; }
.store_address { color: #67676f; font-size: 1.5rem; line-height: 1.45; }

.sf_store_filters {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 2px;
}

.sf_store_filters i {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 2px 8px;
    border-radius: 99px;
    background: #f2f2f4;
    color: #666;
    font-size: 1.2rem;
    font-style: normal;
    line-height: 1.4;
    box-sizing: border-box;
}

.store_map_pc { width: 450px; position: sticky; top: 0; padding: 24px 0; }
.map_box_sticky {
    width: 100%;
    height: 450px;
    border: 1px solid #e5e5e9;
    border-radius: 12px;
    background-color: #e8e8ee;
    overflow: hidden;
    position: relative;
}

.map_box_sticky :deep(.kakao-map) {
    height: 100%;
    border: none;
    border-radius: 12px;
}

.btn_map_close { position: absolute; top: 12px; right: 12px; background: rgba(0,0,0,0.6); color: #fff; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.store_map_mobile { display: none; }

@media (max-width: 768px) {
    .store_find_modal.modal_cont {
        width: 100%;
    }

    .store_find_modal .modal_content {
        margin-top: -40px;
        padding-top: 340px;
    }

    .sf_row_dd {
        flex-direction: row;
    }

    .sf_select {
        width: 100%;
        height: 48px;
    }

    .sf_chip_wrap {
        min-height: 0;
    }

    .sf_map_canvas {
        overflow: initial;
    }

    .sf_map_canvas .inner {
        display: block;
    }

    .sf_map_canvas .store_list {
        max-height: initial;
    }

    .sf_map_canvas .map_canvas {
        border-radius: 0;
        height: 320px;
        min-height: 320px;
        position: fixed;
        top: 61.2px;
        right: 0;
        left: 0;
        z-index: 2;
    }

    .store_layout_flex {
        flex-direction: column;
    }

    .store_map_pc {
        display: none;
    }

    .store_map_mobile {
        display: block;
        width: 100%;
        margin-top: 20px;
    }

    .store_map_mobile .map_box {
        width: 100%;
        height: 250px;
        border: 1px solid #e5e5e9;
        border-radius: 12px;
        overflow: hidden;
    }

    .store_map_mobile .map_box :deep(.kakao-map) {
        height: 100%;
        border: none;
        border-radius: 12px;
    }
}
</style>
